import React, { Fragment, useRef, useState } from 'react';
import axios from 'axios';
import { Card, CardBody, CardHeader, Col, Container, Row, Button, Alert } from 'reactstrap';
import { UploadCloud, X, FileText } from 'react-feather';
import { Breadcrumbs } from '../../../../AbstractElements';
import API_BASE_URL from '../../../../Config/api';

const UploadOrder = () => {
  const [file, setFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [status, setStatus] = useState(null); // { type, text }
  const [dragging, setDragging] = useState(false);
  const inputRef = useRef(null);

  const selectFile = (f) => {
    if (!f) return;
    setFile(f);
    setStatus(null);
  };

  const handleFileChange = (e) => selectFile(e.target.files[0]);

  const handleDrop = (e) => {
    e.preventDefault();
    setDragging(false);
    selectFile(e.dataTransfer.files[0]);
  };

  const clearFile = () => {
    setFile(null);
    setStatus(null);
    if (inputRef.current) inputRef.current.value = '';
  };

  const handleUpload = async () => {
    if (!file) return setStatus({ type: 'danger', text: 'Please select a file first.' });

    const formData = new FormData();
    formData.append('file', file);

    try {
      setUploading(true);
      setStatus(null);
      const res = await axios.post(`${API_BASE_URL}/api/upload-order`, formData);
      setStatus({ type: 'success', text: res.data.message || 'Upload complete' });
    } catch (err) {
      console.error(err);
      setStatus({ type: 'danger', text: err.response?.data?.error || 'Error uploading file' });
    } finally {
      setUploading(false);
    }
  };

  return (
    <Fragment>
      <Breadcrumbs mainTitle="Upload Order" parent="Orders" title="Upload Order" />
      <Container fluid={true}>
        <Row>
          <Col xl="8" lg="10" className="mx-auto">
            <Card>
              <CardHeader>
                <h4>Upload Order Form</h4>
                <span>Upload an order Excel (.xlsx / .xls) to import an order.</span>
              </CardHeader>
              <CardBody>
                <div
                  className={`rf-dropzone ${dragging ? 'is-dragging' : ''}`}
                  role="button"
                  tabIndex={0}
                  onClick={() => inputRef.current && inputRef.current.click()}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); inputRef.current && inputRef.current.click(); } }}
                  onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                  onDragLeave={() => setDragging(false)}
                  onDrop={handleDrop}
                >
                  <div className="rf-dropzone-icon"><UploadCloud /></div>
                  <h6>Drag &amp; drop your file here, or <span className="rf-browse">browse</span></h6>
                  <small>Accepted formats: .xlsx, .xls</small>
                </div>
                <input
                  ref={inputRef}
                  type="file"
                  className="d-none"
                  accept=".xlsx, .xls"
                  onChange={handleFileChange}
                />

                {file && (
                  <div className="mt-3 d-flex align-items-center gap-2 flex-wrap">
                    <span className="rf-file-chip">
                      <FileText size={16} />
                      {file.name}
                      <span className="rf-file-remove" role="button" aria-label="Remove file" onClick={clearFile}>
                        <X size={15} />
                      </span>
                    </span>
                  </div>
                )}

                {status && (
                  <Alert color={status.type} className="mt-3 mb-0">{status.text}</Alert>
                )}

                <div className="d-flex align-items-center gap-2 mt-4">
                  <Button color="primary" onClick={handleUpload} disabled={!file || uploading}>
                    {uploading ? 'Uploading…' : 'Upload File'}
                  </Button>
                  {file && !uploading && (
                    <Button color="link" onClick={clearFile}>Clear</Button>
                  )}
                </div>
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </Fragment>
  );
};

export default UploadOrder;
